/*!
 * synthTimbre.js — fm:, drive: and the drum waves of synth()
 * © 2026 Rob Canning — GPLv3
 *
 *     synth(freq:~A2, fm:{ratio:2, index:3})              a modulator at twice the note
 *     synth(degree:..., fm:{ratio:3.5, index:6, decay:0.4}, envPerStep:1)   bells
 *     synth(wave:saw, drive:0.6, filter:{type:lp, freq:900})   grit, then tamed
 *     synth(wave:kick, dur:1)   synth(wave:snare, dur:2)   synth(wave:hat, dur:0.25)
 *
 * FM. Each oscillator gets a modulator (a sine) at ratio × its note, whose
 * swing is index × the modulator's frequency -- the classic index: 0 a plain
 * tone, 1-3 warm, 5+ bright and metallic. Integer ratios are harmonic (2
 * hollow, 3 reedy), others are bells. decay: lets the index fall away after
 * each note (each step), the way a struck sound loses its brightness.
 *
 * The note's frequency is not set on the oscillator but on a pitch source
 * (a ConstantSourceNode) that drives the oscillator, the modulator's
 * frequency and its depth together; it stands in as the oscillator's
 * `frequency`, so everything synth() does to a frequency -- steps, glide,
 * chords, rests -- moves the modulator with it.
 *
 * drive: 0..1 a soft clip (tanh) between the source and the envelope.
 *
 * Drums: wave:kick a sine falling from three times its note (default 50 Hz)
 * at each step; snare and hat filtered noise; all three short and struck
 * every step (envPerStep). Anything written on the line wins.
 */

const realFrequency = typeof OscillatorNode !== "undefined"
  ? Object.getOwnPropertyDescriptor(OscillatorNode.prototype, "frequency")?.get
  : null;

const num = (v, d) => (Number.isFinite(Number(v)) ? Number(v) : d);

/** fm:{ratio, index, decay} (or fm:<index>), or null for none. */
export function fmSpec(fm) {
  if (fm == null || fm === false || fm === 0) return null;
  const o = typeof fm === "object" && !Array.isArray(fm) ? fm : { index: fm };
  const ratio = Math.max(0, num(o.ratio, 1));
  const index = Math.max(0, num(o.index, 2));
  const decay = Math.max(0, num(o.decay, 0));
  return { ratio, index, decay };
}

/** Give an oscillator a modulator (spec from fmSpec). Call before start(). */
export function attachFM(ctx, osc, spec, track = () => {}) {
  if (!spec || !realFrequency || osc._fm) return osc;
  const carrier = realFrequency.call(osc);
  const pitch = ctx.createConstantSource();
  pitch.offset.value = carrier.value;
  carrier.value = 0;
  pitch.connect(carrier);

  const mod = ctx.createOscillator();
  mod.frequency.value = 0;
  const toModHz = ctx.createGain();
  toModHz.gain.value = spec.ratio;
  pitch.connect(toModHz).connect(mod.frequency);

  const depth = ctx.createGain();
  depth.gain.value = 0;
  mod.connect(depth).connect(carrier);
  const toDepth = ctx.createGain();
  toDepth.gain.value = spec.index * spec.ratio;
  pitch.connect(toDepth).connect(depth.gain);

  // the pitch source stands in for the oscillator's frequency
  Object.defineProperty(osc, "frequency", { value: pitch.offset, configurable: true });
  const start = osc.start.bind(osc), stop = osc.stop.bind(osc);
  osc.start = (t) => { start(t); pitch.start(t); mod.start(t); };
  osc.stop = (t) => {
    stop(t);
    try { pitch.stop(t); } catch { /* not started */ }
    try { mod.stop(t); } catch { /* not started */ }
  };
  osc._fm = { spec, toDepth };
  track(pitch); track(mod);
  return osc;
}

/** At a note (a step) at time t: the index from full, falling over decay. */
export function fmStrike(osc, t) {
  const f = osc?._fm;
  if (!f || !(f.spec.decay > 0)) return;
  const g = f.toDepth.gain, full = f.spec.index * f.spec.ratio;
  g.cancelScheduledValues(t);
  g.setValueAtTime(full, t);
  g.setTargetAtTime(0, t, f.spec.decay / 3);
}

/** A soft-clip curve for drive 0..1 (null: none). */
export function driveCurve(drive) {
  const d = Math.min(1, Math.max(0, num(drive, 0)));
  if (!(d > 0)) return null;
  const k = 1 + d * 30, n = 1024, c = new Float32Array(n), norm = Math.tanh(k);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = Math.tanh(k * x) / norm;
  }
  return c;
}

export const DRUMS = new Set(["kick", "snare", "hat"]);

/** The params of a drum wave, with what the line says on top. */
export function drumParams(p) {
  const w = String(p.wave ?? "").toLowerCase();
  if (!DRUMS.has(w)) return p;
  const base = {
    kick:  { wave: "sine",  freq: 50, env: { a: 0.001, d: 0.35, s: 0, r: 0.03 }, _drum: "kick" },
    snare: { wave: "noise", filter: { type: "bp", freq: 1800, q: 0.7 }, env: { a: 0.001, d: 0.16, s: 0, r: 0.03 }, _drum: "snare" },
    hat:   { wave: "noise", filter: { type: "hp", freq: 7000 }, env: { a: 0.001, d: 0.04, s: 0, r: 0.01 }, _drum: "hat" },
  }[w];
  const out = { envPerStep: true, amp: 0.15, ...base };
  for (const [k, v] of Object.entries(p)) if (k !== "wave" && v != null) out[k] = v;
  return out;
}

/** A kick at time t: its pitch falls from three times the note to the note. */
export function kickStrike(param, hz, t) {
  if (!param || !(hz > 0)) return;
  param.cancelScheduledValues(t);
  param.setValueAtTime(hz * 3, t);
  param.exponentialRampToValueAtTime(hz, t + 0.06);
}
